import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { API, TOKEN_KEY } from "@/lib/api";

const LiveCtx = createContext({ status: "off", online: true, subscribe: () => () => {} });
export const useLive = () => useContext(LiveCtx);

// One authenticated WebSocket per tab (/api/ws). Subscribers get every server message plus {type:"reconnected"}.
export function LiveProvider({ hubId, children }) {
  const [status, setStatus] = useState("connecting");
  const [online, setOnline] = useState(navigator.onLine);
  const subs = useRef(new Set());
  const st = useRef({ ws: null, blocked: false, tries: 0, timer: null, alive: true });

  const emit = (m) => subs.current.forEach((fn) => fn(m));

  const connect = useCallback(() => {
    const s = st.current;
    clearTimeout(s.timer);
    if (!s.alive || s.ws) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token || s.blocked || !navigator.onLine) {
      setStatus("reconnecting");
      s.timer = setTimeout(connect, 3000);
      return;
    }
    const ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws?token=${encodeURIComponent(token)}&organization_id=${encodeURIComponent(hubId)}`);
    s.ws = ws;
    let ping;
    ws.onopen = () => {
      s.tries = 0;
      setStatus("live");
      ping = setInterval(() => ws.readyState === 1 && ws.send("ping"), 25000);
      emit({ type: "reconnected" });
    };
    ws.onmessage = (ev) => {
      try { emit(JSON.parse(ev.data)); } catch (_) { /* ignore non-JSON */ }
    };
    ws.onclose = () => {
      clearInterval(ping);
      if (s.ws !== ws) return;
      s.ws = null;
      if (!s.alive) return;
      setStatus("reconnecting");
      s.tries += 1;
      s.timer = setTimeout(connect, Math.min(8000, 1000 * s.tries));
    };
    ws.onerror = () => ws.close();
  }, [hubId]);

  useEffect(() => {
    const s = st.current;
    s.alive = true;
    connect();
    const drop = () => {
      const w = s.ws;
      s.ws = null;
      w?.close();
      setStatus("reconnecting");
      s.timer = setTimeout(connect, 3000);
    };
    // Test hook: window.__queueLive.block(true) cuts the socket; block(false) restores it.
    window.__queueLive = { block: (b) => { s.blocked = !!b; if (b) drop(); else { clearTimeout(s.timer); connect(); } } };
    const on = () => { setOnline(true); connect(); };
    const off = () => { setOnline(false); drop(); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      s.alive = false;
      clearTimeout(s.timer);
      const w = s.ws;
      s.ws = null;
      w?.close();
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, [connect]);

  const subscribe = useCallback((fn) => {
    subs.current.add(fn);
    return () => subs.current.delete(fn);
  }, []);

  return <LiveCtx.Provider value={{ status, online, subscribe }}>{children}</LiveCtx.Provider>;
}

// Calls fn on matching live events, and every `pollMs` while the socket is down (pollMs=null: no polling).
export function useLiveRefresh(fn, match, pollMs = 5000) {
  const { status, subscribe } = useLive();
  const ref = useRef(fn);
  ref.current = fn;
  const matchRef = useRef(match);
  matchRef.current = match;
  useEffect(() => subscribe((m) => { if (m.type === "reconnected" || matchRef.current(m)) ref.current(m); }), [subscribe]);
  useEffect(() => {
    if (status === "live" || !pollMs) return undefined;
    const id = setInterval(() => ref.current({ type: "poll" }), pollMs);
    return () => clearInterval(id);
  }, [status, pollMs]);
}
